import { awaitDataServiceFetcher } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/DataServiceFetchers';
import { IPayrollRepository } from '../entities/IPayrollRepository';

import {
  CreateProcessPayrollFileAuthorizationResponse,
  Payroll,
  PayrollDetails,
  PayrollExecuteVopRequestBody,
  PayrollExecuteVopResponse,
  PayrollFileType,
  PayrollTotalAmount,
  PayrollUploadDataFileTypeRequest,
  PayrollUploadFileTypeResponse,
  ProcessPayrollAuthorizationResponse,
} from '../entities/types';

class PayrollRepositoryImpl implements IPayrollRepository {
  constructor() {
    this.ProcessPayrollAuthorization = this.ProcessPayrollAuthorization.bind(this);
    this.UploadFile = this.UploadFile.bind(this);
    this.ExecuteVop = this.ExecuteVop.bind(this);
    this.CreateProcessPayrollFileAuthorization = this.CreateProcessPayrollFileAuthorization.bind(this);
    this.GetPayrollTotalAmount = this.GetPayrollTotalAmount.bind(this);
    this.GetPayrollList = this.GetPayrollList.bind(this);
    this.GetPayrollFileTypes = this.GetPayrollFileTypes.bind(this);
    this.GetPayrollDetails = this.GetPayrollDetails.bind(this);
    this.DeletePayroll = this.DeletePayroll.bind(this);
  }

  ProcessPayrollAuthorization(customerId: number, requestId: string): Promise<ProcessPayrollAuthorizationResponse> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'PUT',
        url: `/secure-web/api/private/bulk/payroll/file/process/${customerId}/${requestId}`,
        dataAdapter: data => {
          if (!data.body) {
            throw new Error('Error');
          }
          return data.body;
        },
      },
    });
  }

  UploadFile(customerId: number, data: PayrollUploadDataFileTypeRequest): Promise<PayrollUploadFileTypeResponse> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        data: { ...data, executeVop: undefined },
        params: {
          executeVop: data.executeVop,
        },
        url: `/secure-web/api/private/bulk/payroll/${customerId}/file`,
        dataAdapter: data => {
          if (!data.body) {
            throw new Error('Error');
          }
          return data.body;
        },
      },
    });
  }

  ExecuteVop(data: PayrollExecuteVopRequestBody): Promise<PayrollExecuteVopResponse> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        data: {
          ...data,
          entries: data.entries.map(item => ({
            rowSn: item.rowSn,
            iban: item.iban,
            bic: item.bic,
            beneficiaryName: item.beneficiaryName,
          })),
        },
        url: `/secure-web/api/private/bulk/vop/verify`,
        dataAdapter: data => {
          if (!data.body) {
            throw new Error('Error');
          }

          return data.body.vopBulkResults;
        },
      },
    });
  }

  CreateProcessPayrollFileAuthorization(
    customerId: number,
    fileId: number,
  ): Promise<CreateProcessPayrollFileAuthorizationResponse> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/api/private/bulk/payroll/file/${customerId}/${fileId}/initiate`,
        dataAdapter: data => {
          if (!data.body) {
            throw new Error('Error');
          }
          return data.body;
        },
      },
    });
  }

  GetPayrollTotalAmount(payrollBulkHeaderId: number): Promise<PayrollTotalAmount> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/bulk/payroll/${payrollBulkHeaderId}/total`,
        dataAdapter: data => {
          if (!data.body) {
            throw new Error('Error');
          }
          return data.body;
        },
      },
    });
  }

  GetPayrollList(customerId: number, dateFrom: string, dateTo: string): Promise<Payroll[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        params: {
          dateFrom,
          dateTo,
        },
        url: `/secure-web/api/private/bulk/payroll/list/${customerId}`,
        dataAdapter: data => {
          if (!data.body) {
            throw new Error('Error');
          }
          return data.body.payrollList;
        },
      },
    });
  }

  GetPayrollFileTypes(): Promise<PayrollFileType[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: '/secure-web/api/private/bulk/payroll/fileTypes',
        dataAdapter: data => {
          if (!data.body) {
            throw new Error('Error');
          }
          return data.body.payrollFileTypeDataList.map(({ fileType }: { fileType: string }) => ({
            value: fileType,
            label: fileType,
          }));
        },
      },
    });
  }

  GetPayrollDetails(customerId: number, fileId: number): Promise<PayrollDetails> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/bulk/payroll/details/${customerId}/${fileId}`,
        dataAdapter: data => {
          if (!data.body) {
            throw new Error('Error');
          }
          return data.body;
        },
      },
    });
  }

  DeletePayroll(payrollBulkHeaderId: number): Promise<any> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'DELETE',
        url: `/secure-web/api/private/bulk/payroll/${payrollBulkHeaderId}`,
      },
    });
  }
}

export { PayrollRepositoryImpl };
